import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Button, Form, Modal } from "semantic-ui-react";
import { useMessageResources, useTextResources } from "../../language";
import PropTypes from "prop-types";
import NumericInput from "../shared/NumericInput";
import RoomTypesDropDown from "../shared/RoomTypesDropDown";
import CustomerDropDown from "../shared/CustomerDropDown";
import RatesDropDown from "../shared/RatesDropDown";
import { DatePicker } from "../shared/DatePicker";
import { getDaysOfDatesRange, isNonEmptyArray, toServerDateFormat } from "../shared/utils";
import { onSuccess, onWarning } from "../shared/messages";
import EntityTypes from "../../pages/GroupReservation/Constants/EntityTypes";
import { AllocationAndStrategy } from "../../api/services/next";
import moment from "moment";
import { SyncChannelManagerProducts } from "../../api/services/pms";
import { onError } from "../shared/messages";
import { useLoader } from "../../hooks/useLoader";

export const AllocationCreationForStrategyPopup = ({
  refreshData,
  open,
  onClose,
  strategy,
  onApplyChanges,
}) => {
  const loader = useLoader();
  const text = useTextResources();
  const message = useMessageResources();
  const [allocation, setAllocation] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setAllocation({
      AllocatedRooms: 0,
      Priority: 0,
      StartDate: new Date(),
      EndDate: new Date(),
    });
  }, [open]);

  const roomTypesSpec = useMemo(
    () => ({ HotelId: strategy.HotelId, RateIds: allocation.RateId }),
    [allocation.RateIds, strategy?.HotelId]
  );
  const ratesSpec = useMemo(
    () => ({ HotelId: strategy.HotelId, RoomTypeIds: allocation.RoomTypeId }),
    [allocation.RoomTypeId, strategy?.HotelId]
  );

  const getMissingFields = useCallback((allocationObj) => {
    let missingFields = [];
    if (!allocationObj.RoomTypeId)
      missingFields.push(text("RoomType"));
    if (!allocationObj.StartDate)
      missingFields.push(text("StartDate"));
    if (!allocationObj.EndDate)
      missingFields.push(text("EndDate"));
    if (!allocationObj.AllocatedRooms)
      missingFields.push(text("NumberAllocatedRooms"));
    if (!allocationObj.RateId)
      missingFields.push(text("Rate"));
    if (!isNonEmptyArray(allocationObj.Customers))
      missingFields.push(text("Customers"));
    return missingFields;
  }, [text]);

  const onSave = useCallback(async () => {
    let missingFields = getMissingFields(allocation);
    if (missingFields.length !== 0) {
      onWarning(text("MissingFields").replace('{0}', missingFields.join(", ")));
      return;
    }

    try {
      setIsLoading(true);
      //creating StrategyAllocation
       const strategyAllocation = {
        Priority: allocation.Priority,
        StrategyId: strategy.StrategyId,
        StartDate: toServerDateFormat(allocation.StartDate),
        EndDate: toServerDateFormat(allocation.EndDate),
        allocation: {
          HotelId: strategy.HotelId,
          WingId: strategy.WingId,
          AllocationEntities: [
            ...allocation.Customers.map((customer) => ({
              EntityId: customer.Id,
              EntityTypeId: EntityTypes.Customer,
            })),
            { EntityId: allocation.RateId, EntityTypeId: EntityTypes.Rate },
            { EntityId: allocation.RoomTypeId, EntityTypeId: EntityTypes.RoomType },
          ],
        },
      };
      const allocationId = await AllocationAndStrategy.AddStrategyAllocation(strategyAllocation);
      //creating AllocationDates
      const allocationDates = getDaysOfDatesRange(allocation.StartDate, allocation.EndDate).map(date => ({
        AllocationId: allocationId,
        RoomTypeId: allocation.RoomTypeId,
        Date: toServerDateFormat(moment(date)),
        AllocatedRooms: allocation.AllocatedRooms,
      }))
      await AllocationAndStrategy.UpdateAllocationDates(allocationDates);

      try {
        loader(true);
        await SyncChannelManagerProducts({
          HotelID: strategy.HotelId,
          RoomTypeIds: allocation.RoomTypeId ? [allocation.RoomTypeId] : null,
          RateIds: allocation.RateId ? [allocation.RateId] : null,
          StartDate: toServerDateFormat(allocation.StartDate),
          EndDate: toServerDateFormat(allocation.EndDate),
          IsUpdateAvailabilty: true,
          RecalculateAvailabilty: true,
          Reason: `Strategy allocation created, Allocation ID: ${allocationId}`,
          TriggerOperation: "Strategy Allocation Update"
        })
      } catch (error) {
        onError(message(error));
      } finally {
        loader(false);
      }

      await refreshData();
      onApplyChanges && onApplyChanges();
      onSuccess();
      onClose();
    } catch (error) {
      onWarning(message(error.error?.detail));
    } finally {
      setIsLoading(false);
    }
  }, [allocation, getMissingFields, message, text, onApplyChanges, onClose, refreshData]);

  const changeAllocationField = useCallback((fieldName, value) => {
    setAllocation(prevState => ({ ...prevState, [fieldName]: value }));
  }, []);

  const setRoomType = useCallback((value) => changeAllocationField("RoomTypeId", value), []);
  const setCustomers = useCallback((customers) => changeAllocationField("Customers", customers), []);
  const setRate = useCallback((value) => changeAllocationField("RateId", value), []);
  const setAllocatedRooms = useCallback((value) => changeAllocationField("AllocatedRooms", parseInt(value)), []);
  const setPriority = useCallback((value) => changeAllocationField("Priority", parseInt(value)), []);

  const setStartDate = useCallback((newStartDate) => {
    setAllocation((prevState) => ({
      ...prevState,
      StartDate: newStartDate,
      EndDate: newStartDate > prevState.EndDate ? newStartDate : prevState.EndDate,
    }));
  }, []);

  const setEndDate = useCallback((newEndDate) => {
    setAllocation((prevState) => ({
      ...prevState,
      StartDate: newEndDate < prevState.StartDate ? newEndDate : prevState.StartDate,
      EndDate: newEndDate,
    }));
  }, []);

  return (
    <Modal size="small" open={open} onClose={onClose}>
      <Modal.Header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {`${text("AddAllocationToStrategy")} ${strategy?.StrategyName}`}
        <Button circular icon="close" onClick={onClose} />
      </Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Field
            label={text("RoomType")}
            placeholder={text("Select")}
            control={RoomTypesDropDown}
            onChange={setRoomType}
            spec={roomTypesSpec}
            search
          />
          <Form.Field
            label={text("Customer")}
            placeholder={text("Select")}
            control={CustomerDropDown}
            multiple
            clearable
            onChangeItem={setCustomers}
          />
          <Form.Field
            label={text("Rate")}
            placeholder={text("Select")}
            control={RatesDropDown}
            onChange={setRate}
            spec={ratesSpec}
            search
          />
          <Form.Group widths="equal">
            <Form.Field
              label={text("Rooms")}
              control={NumericInput}
              value={allocation.AllocatedRooms}
              onChange={setAllocatedRooms}
              min={0}
              digits={0}
            />
            <Form.Field
              label={text("Priority")}
              control={NumericInput}
              value={allocation.Priority}
              onChange={setPriority}
              min={0}
              digits={0}
            />
          </Form.Group>
          <Form.Group widths="equal">
            <DatePicker
              label={text("From")}
              onChange={setStartDate}
              value={allocation.StartDate}
              clearable={false}
              minDate={moment().subtract(1, "day").toDate()}
            />
            <DatePicker
              label={text("To")}
              onChange={setEndDate}
              value={allocation.EndDate}
              clearable={false}
              minDate={moment().subtract(1, "day").toDate()}
            />
          </Form.Group>
        </Form>
      </Modal.Content>
      <Modal.Actions style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button positive onClick={onSave} loading={isLoading}>
          {text("Create")}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

AllocationCreationForStrategyPopup.propTypes = {
  refreshData: PropTypes.func,
  strategy: PropTypes.object,
  open: PropTypes.bool,
  onCancelChanges: PropTypes.func,
  onApplyChanges: PropTypes.func,
  onClose: PropTypes.func,
};
